/*
 * component-colorway-carousel.js
 * ---------------------------------------------------------------------------
 * Keeps product-card carousels in step with the card's colorway.
 *
 * 1. B4B faced-out cards
 *    B4B Variant Products builds colorway cards in the browser by cloning the
 *    product card and tagging the wrapper with data-b4b-color /
 *    data-variant-id. product__card.liquid renders one
 *    <template data-colorway-slides> per colour that has variant-level
 *    [Card] Carousel Images, plus a "default" one holding the product-level
 *    slides. Slides 2+ of each colorway card are swapped for the matching set.
 *    Slide 1 is left to B4B, which already sets it to the colorway's image.
 *
 * 2. Colour swatch clicks
 *    On carousel cards the swatches used to change only the link, never the
 *    images. Clicking one now shows that colour's image on slide 1 (from the
 *    swatch's data-img) and that colour's slides after it, then goes back to
 *    slide 1.
 *
 * 3. B4B colorway cards also show the colour in the title
 *    ("Cottage Salt Cellar – Navy") and hide their swatches.
 *
 * Loaded once per card (the same pattern as component-swiper.js), so it guards
 * itself to run only once.
 */
(() => {
  if (window.__fpColorwayCarousel) return;
  window.__fpColorwayCarousel = true;

  const norm = (s) => (s || '').trim().toLowerCase();

  function templateFor(card, { color, variantId }) {
    const templates = [...card.querySelectorAll('template[data-colorway-slides]')];
    if (!templates.length) return null;
    const c = norm(color);
    return (
      templates.find((t) => variantId && t.dataset.variantIds.split(' ').includes(String(variantId))) ||
      templates.find((t) => t.dataset.color === c) ||
      templates.find((t) => t.dataset.color === 'default')
    );
  }

  /* Swaps slides 2+ for the template's set and, optionally, slide 1's image.
     Returns false if Swiper isn't ready yet, so the caller can retry. */
  function showColorway(card, { color, variantId, firstImage }) {
    const carousel = card.querySelector('swiper-carousel');
    const wrapperEl = carousel && carousel.querySelector('.swiper-wrapper');
    const swiper = carousel && carousel.swiper;
    if (!wrapperEl || !swiper) return false;

    const slides = [...wrapperEl.querySelectorAll(':scope > .swiper-slide')];

    if (firstImage) {
      const img = slides[0] && slides[0].querySelector('img');
      if (img) {
        img.removeAttribute('srcset');
        img.src = firstImage;
      }
    }

    // No templates means the product has no colorway images, so slides 2+
    // are the shared product-level ones and stay as they are.
    const tpl = templateFor(card, { color, variantId });
    if (tpl) {
      // The theme's Swiper build has no Manipulation module (removeSlide /
      // appendSlide), so edit the DOM and let update() recount. swiper.slides
      // is empty on cards that were hidden when Swiper started, so work from
      // the wrapper's children.
      slides.slice(1).forEach((slide) => slide.remove());
      tpl.content
        .cloneNode(true)
        .querySelectorAll('.swiper-slide')
        .forEach((slide) => wrapperEl.appendChild(slide));
    }

    swiper.update();
    swiper.slideTo(0, 0);
    return true;
  }

  /* Selected-swatch style.
     B4B builds colorway cards by cloning a card that Alpine has already
     styled, so the clone's swatches arrive with "border-opacity-25
     text-opacity-75" baked into their class attribute. Alpine's :class only
     removes classes it added itself, so on cloned cards a swatch that started
     unselected could never look selected again. This keeps it in sync by hand.
     Alpine's binding still runs alongside this and never contradicts it. */
  const OFF = ['border-opacity-25', 'text-opacity-75'];
  function syncSwatches(card, selected) {
    card.querySelectorAll('.vart-btn').forEach((btn) => {
      const on = norm(btn.title) === norm(selected);
      OFF.forEach((cls) => btn.classList.toggle(cls, !on));
    });
  }

  /* B4B colorway cards: title shows the colour, swatches hidden.
     Each card already is one colour, so swatches would only let a "Navy"
     card turn into Forest. The rule is keyed off B4B's data-b4b-color, which
     only faced-out cards get, so normal cards keep their swatches. It's
     injected here (once) because base.css is a compiled build file. */
  const style = document.createElement('style');
  style.textContent = '[data-b4b-color] [data-card-swatches]{display:none !important}';
  document.head.appendChild(style);

  function setColorwayTitle(card, color) {
    const el = card.querySelector('[data-card-variant-title]');
    if (!el) return;
    el.textContent = ' – ' + color;
    el.hidden = false;
  }

  /* 1. B4B faced-out cards */
  function applyB4B(wrapper) {
    const color = wrapper.dataset.b4bColor;
    if (!color) return;

    const card = wrapper.querySelector('.product__card');
    if (!card) return;

    if (wrapper.dataset.swatchSynced !== color) {
      syncSwatches(card, color);
      setColorwayTitle(card, color);
      wrapper.dataset.swatchSynced = color;
    }

    // Already done for this colour. B4B clones carry this attribute over from
    // the card they were copied from, so compare against the colour itself.
    if (wrapper.dataset.colorwayApplied === color) return;
    if (!card.querySelector('template[data-colorway-slides]')) return;

    if (showColorway(card, { color, variantId: wrapper.dataset.variantId })) {
      wrapper.dataset.colorwayApplied = color;
    } // else: Swiper not ready; the next DOM change retries
  }

  let queued = false;
  function scan() {
    queued = false;
    document.querySelectorAll('[data-b4b-color]').forEach(applyB4B);
  }
  function queue() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(scan);
  }

  /* 2. Colour swatch clicks (product__color-selector renders .vart-btn) */
  function withWidth(url, width) {
    if (!url) return url;
    return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('.vart-btn');
    if (!btn) return;
    const card = btn.closest('.product__card');
    if (!card) return;

    syncSwatches(card, btn.title);

    if (!card.querySelector('swiper-carousel')) return; // single-image cards swap via Alpine
    showColorway(card, {
      color: btn.title,
      firstImage: withWidth(btn.dataset.img, 800),
    });
  });

  function start() {
    queue();
    // B4B builds its cards after load, and again on infinite scroll or filtering.
    new MutationObserver(queue).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-b4b-color', 'data-variant-id'],
    });
  }

  customElements.whenDefined('swiper-carousel').then(() => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  });
})();